import React from 'react';
import { Box, Container } from '@mui/material';
import Grid from '@mui/material/Grid';
import { SBlock, SCircle, SText, SPanel, SRow, SkeletonScreen } from './SkeletonKit';

/**
 * One skeleton per page shape. Each mirrors the real layout - same container
 * widths, same column split, same card rhythm - so content does not jump into
 * place when the request resolves.
 */

const range = (n: number) => Array.from({ length: n });

/* ------------------------------------------------------------------ */
/* Communities                                                         */
/* ------------------------------------------------------------------ */

/** Just the posts column - used while only the feed is refetching. */
export const PostListSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} role="status" aria-busy="true" aria-label="Loading posts">
    {range(count).map((_, i) => (
      <SPanel key={i} r="20px" p={0}>
        <Box sx={{ p: 3 }}>
          <SRow avatar={44} />
          <Box sx={{ mt: 2.5 }}><SBlock h={18} w="70%" /></Box>
          <Box sx={{ mt: 1.5 }}><SText lines={2} /></Box>
        </Box>
        <Box sx={{ p: 3, pt: 0, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {range(4).map((__, j) => <SBlock key={j} h={32} w={72} r="16px" />)}
        </Box>
      </SPanel>
    ))}
  </Box>
);

/** The right-hand sidebar: trending communities + top contributors. */
export const CommunitySidebarSkeleton: React.FC = () => (
  <Box
    role="status"
    aria-busy="true"
    aria-label="Loading community highlights"
    sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', minWidth: 0 }}
  >
    {range(2).map((_, i) => (
      <SPanel key={i}>
        <Box sx={{ mb: 2.5 }}><SBlock h={16} w="60%" /></Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {range(4).map((__, j) => <SRow key={j} />)}
        </Box>
      </SPanel>
    ))}
  </Box>
);

/** The communities list column on its own. */
export const CommunityListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <Box role="status" aria-busy="true" aria-label="Loading communities">
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {range(count).map((_, i) => <SRow key={i} avatar={44} />)}
    </Box>
  </Box>
);

/** A plain list of avatar rows inside one panel - members, followers, etc. */
export const RowListSkeleton: React.FC<{ count?: number; avatar?: number; label?: string }> = ({
  count = 8,
  avatar = 44,
  label = 'Loading list',
}) => (
  <SPanel r="20px" sx={{ minHeight: 300 }}>
    <Box role="status" aria-busy="true" aria-label={label} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {range(count).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <SRow avatar={avatar} />
          <SBlock h={30} w={92} r="15px" sx={{ flexShrink: 0 }} />
        </Box>
      ))}
    </Box>
  </SPanel>
);

/** A responsive grid of cards. Defaults to the 3-up card layout. */
export const CardGridSkeleton: React.FC<{
  count?: number;
  size?: { xs?: number; sm?: number; md?: number; lg?: number };
  label?: string;
}> = ({ count = 6, size = { xs: 12, sm: 6, md: 4 }, label = 'Loading' }) => (
  <Grid container spacing={3} sx={{ width: '100%' }} role="status" aria-busy="true" aria-label={label}>
    {range(count).map((_, i) => (
      <Grid key={i} size={size} sx={{ minWidth: 0 }}>
        <SPanel r="20px">
          <SRow avatar={48} />
          <Box sx={{ mt: 2 }}><SText lines={2} /></Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <SBlock h={26} w={72} r="13px" />
            <SBlock h={26} w={56} r="13px" />
          </Box>
        </SPanel>
      </Grid>
    ))}
  </Grid>
);

/** Threaded comments under a post. */
export const CommentListSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Box role="status" aria-busy="true" aria-label="Loading comments" sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
    {range(count).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 2, minWidth: 0 }}>
        <SCircle size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SBlock h={12} w="30%" />
          <Box sx={{ mt: 1 }}><SText lines={2} h={11} /></Box>
        </Box>
      </Box>
    ))}
  </Box>
);

/* ------------------------------------------------------------------ */
/* Community detail                                                    */
/* ------------------------------------------------------------------ */

export const CommunityDetailSkeleton: React.FC = () => (
  <SkeletonScreen label="Loading community">
    <SBlock h={260} r="0" />
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 4 }, mt: { xs: -8, md: -10 } }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid size={{ xs: 12, md: 10, lg: 8 }} sx={{ minWidth: 0 }}>
          <SPanel sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <SCircle size={96} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <SBlock h={26} w="45%" />
                <Box sx={{ mt: 1.5 }}><SText lines={2} /></Box>
              </Box>
              <SBlock h={44} w={150} r="50px" />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
              {range(3).map((_, i) => <SBlock key={i} h={14} w={80} />)}
            </Box>
          </SPanel>

          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            {range(4).map((_, i) => <SBlock key={i} h={14} w={i === 0 ? 70 : 90} />)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {range(3).map((_, i) => (
              <SPanel key={i} r="20px">
                <SRow avatar={44} />
                <Box sx={{ mt: 2.5 }}><SBlock h={18} w="65%" /></Box>
                <Box sx={{ mt: 1.5 }}><SText lines={3} /></Box>
              </SPanel>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Container>
  </SkeletonScreen>
);

/* ------------------------------------------------------------------ */
/* Post detail                                                         */
/* ------------------------------------------------------------------ */

export const PostDetailSkeleton: React.FC = () => (
  <SkeletonScreen label="Loading post">
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={4} justifyContent="center">
        <Grid size={{ xs: 12, md: 7, lg: 7 }} sx={{ minWidth: 0 }}>
          <SPanel r="20px">
            <SRow avatar={48} />
            <Box sx={{ mt: 3 }}><SBlock h={28} w="80%" /></Box>
            <Box sx={{ mt: 2 }}><SText lines={4} /></Box>
            <Box sx={{ mt: 3 }}><SBlock h={280} r="16px" /></Box>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {range(4).map((_, i) => <SBlock key={i} h={36} w={84} r="18px" />)}
            </Box>
          </SPanel>

          <Box sx={{ mt: 4, mb: 2 }}><SBlock h={18} w={160} /></Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {range(4).map((_, i) => (
              <SPanel key={i} r="16px" p={2.5}>
                <SRow avatar={36} />
                <Box sx={{ mt: 1.5, pl: 6.5 }}><SText lines={2} /></Box>
              </SPanel>
            ))}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 5 }} sx={{ minWidth: 0 }}>
          <SPanel>
            <Box sx={{ mb: 2.5 }}><SBlock h={16} w="55%" /></Box>
            {range(4).map((_, i) => <Box key={i} sx={{ mb: 2.5 }}><SRow /></Box>)}
          </SPanel>
        </Grid>
      </Grid>
    </Container>
  </SkeletonScreen>
);

/* ------------------------------------------------------------------ */
/* Profile (own + public)                                              */
/* ------------------------------------------------------------------ */

export const ProfileSkeleton: React.FC<{ tabs?: number }> = ({ tabs = 4 }) => (
  <SkeletonScreen label="Loading profile">
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5, lg: 6 }, px: { xs: 2, sm: 3 } }}>
      <SPanel sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <SCircle size={110} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SBlock h={26} w="45%" />
            <Box sx={{ mt: 1.5 }}><SBlock h={14} w="30%" /></Box>
            <Box sx={{ mt: 2 }}><SText lines={2} /></Box>
          </Box>
          <SBlock h={44} w={140} r="50px" />
        </Box>
        <Box sx={{ display: 'flex', gap: 4, mt: 3, flexWrap: 'wrap' }}>
          {range(3).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SBlock h={20} w={50} />
              <SBlock h={11} w={70} />
            </Box>
          ))}
        </Box>
      </SPanel>

      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {range(tabs).map((_, i) => <SBlock key={i} h={14} w={90} />)}
      </Box>

      <Grid container spacing={3}>
        {range(4).map((_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0 }}>
            <SPanel r="20px">
              <SRow avatar={48} />
              <Box sx={{ mt: 2 }}><SText lines={2} /></Box>
            </SPanel>
          </Grid>
        ))}
      </Grid>
    </Container>
  </SkeletonScreen>
);

/* ------------------------------------------------------------------ */
/* Explore / maps                                                      */
/* ------------------------------------------------------------------ */

/**
 * Explore keeps its search bar and map mounted while a search runs - the map
 * has its own overlay - so what is missing during a search is the results
 * list. This fills that space with the same wide cards it will be replaced by.
 */
export const CafeResultsSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <Box sx={{ mt: 8, width: '100%' }} role="status" aria-busy="true" aria-label="Searching for cafes">
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <SBlock h={30} w={220} />
    </Box>
    <Grid container spacing={3} direction="column">
      {range(count).map((_, i) => (
        <Grid key={i} size={{ xs: 12 }} sx={{ minWidth: 0 }}>
          <SPanel r="32px" p={3}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 3 }}>
              <SBlock w={220} h={150} r="24px" sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <SBlock h={22} w="55%" />
                <Box sx={{ mt: 1.5 }}><SBlock h={13} w="80%" /></Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {range(3).map((__, j) => <SBlock key={j} h={26} w={78} r="13px" />)}
                </Box>
              </Box>
            </Box>
          </SPanel>
        </Grid>
      ))}
    </Grid>
  </Box>
);


/* ------------------------------------------------------------------ */
/* Previously viewed cafes                                             */
/* ------------------------------------------------------------------ */

export const PreviouslyViewedSkeleton: React.FC<{ count?: number }> = ({ count = 8 }) => (
  <Box role="status" aria-busy="true" aria-label="Loading visit history" sx={{ width: '100%' }}>
    <Grid container spacing={2.5}>
      {range(count).map((_, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }} sx={{ minWidth: 0 }}>
          <SPanel r="20px" p={0}>
            <SBlock h={160} r="20px 20px 0 0" />
            <Box sx={{ p: 2.5 }}>
              <SBlock h={18} w="75%" />
              <Box sx={{ mt: 1.5 }}><SBlock h={12} w="55%" /></Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <SBlock h={24} w={70} r="12px" />
                <SBlock h={24} w={54} r="12px" />
              </Box>
            </Box>
          </SPanel>
        </Grid>
      ))}
    </Grid>
  </Box>
);

/* ------------------------------------------------------------------ */
/* Landing                                                             */
/* ------------------------------------------------------------------ */

export const LandingSkeleton: React.FC = () => {
  return (
    <SkeletonScreen label="Loading">
      <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, mb: 8 }}>
          <SBlock h={44} w="60%" r="12px" />
          <SBlock h={20} w="40%" />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <SBlock h={48} w={170} r="50px" />
            <SBlock h={48} w={170} r="50px" />
          </Box>
        </Box>
        <SBlock h={380} r="24px" sx={{ mb: 8 }} />
        <Grid container spacing={4}>
          {range(6).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0 }}>
              <SPanel r="20px">
                <SCircle size={52} />
                <Box sx={{ mt: 2 }}><SBlock h={18} w="60%" /></Box>
                <Box sx={{ mt: 1.5 }}><SText lines={3} /></Box>
              </SPanel>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SkeletonScreen>
  );
};

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

/**
 * Rows only - the admin pages already render their own heading and Paper, so
 * this drops straight into the table body's place.
 */
export const AdminTableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({ rows = 8, cols = 5 }) => {
  const cell = `${Math.floor(72 / Math.max(cols - 1, 1))}%`;
  return (
    <Box role="status" aria-busy="true" aria-label="Loading table" sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, pb: 2 }}>
        {range(cols).map((_, i) => <SBlock key={i} h={13} w={i === 0 ? '28%' : cell} />)}
      </Box>
      {range(rows).map((_, i) => (
        <Box key={i} sx={{ py: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: '28%', minWidth: 0 }}><SRow avatar={32} lines={1} /></Box>
          {range(cols - 1).map((__, j) => <SBlock key={j} h={12} w={cell} />)}
        </Box>
      ))}
    </Box>
  );
};

